import { FC } from 'react';
import { Link as RrdLink } from 'react-router-dom';
import { Link, Typography, TypographyProps } from '@mui/material';

import useMetadataContext from '@hooks/useMetadataContext';
import useTitleContext from '@hooks/useTitleContext';
import { Episode, Season, Title } from '@models/Title';

type SeriesCountDisplayProps = Omit<TypographyProps, 'color' | 'title'> & {
  displaySeriesAsLink?: boolean;
  title: Partial<Title>;
};

const getEpisodeIdentifier = (episode: Episode, season: Season) =>
  `S${String(season?.sequenceNumber).padStart(2, '0')} E${String(episode?.sequenceNumber).padStart(
    2,
    '0'
  )}`;

export const EpisodeInfo: FC<SeriesCountDisplayProps> = ({
  alignItems = 'center',
  display = 'flex',
  displaySeriesAsLink,
  gap = 0.5,
  gutterBottom = true,
  lineHeight = 'normal',
  textAlign = 'left',
  title: { type, episode, season, series },
  variant = 'body2',
  ...typographyProps
}) => {
  const { providerIdentifier } = useTitleContext();
  const { getTitleLinkProps } = useMetadataContext();

  if (!(series && episode && season) || type !== 'episode') return null;

  return (
    <Typography
      alignItems={alignItems}
      display={display}
      gap={gap}
      gutterBottom={gutterBottom}
      lineHeight={lineHeight}
      textAlign={textAlign}
      variant={variant}
      {...typographyProps}
    >
      {displaySeriesAsLink ? (
        <>
          <Link
            fontSize='inherit'
            variant='inherit'
            {...{ component: RrdLink }}
            {...getTitleLinkProps({ masterId: series.masterId, providerIdentifier })}
          >
            <strong>{series.titleName}</strong>
          </Link>
        </>
      ) : (
        `${series.titleName} `
      )}
      {getEpisodeIdentifier(episode, season)}
    </Typography>
  );
};
